// Handles on the selected object in the 3D view and in the plan's generated
// section views (docs/claude/handles.md, *Handles in 3D and in sections*). Pure and DOM-free
// (test/handles.test.mjs): what each handle edits, where it stands in each
// space, how a pointer becomes a figure, and the painter the 3D overlay and the
// plan share. The input, the undo step and the rebuild are main.ts's.
//
// Every point here is a WORLD plan point (mm, Y-up) and every `z` a height above
// the ground (mm) — a raised room's base included. A handle's FIGURE is measured
// as its panel row shows it: from its room's floor (a partition's or a heater
// wall's Height from its base), from the ground for anything else (a drawn wall's
// or an extruded shape's Height from its base). Always `z = zero + value`, so the
// figure's zero is `z − value` (`figureZero`).

import type {
  Connection,
  GlassSegment,
  Lattice,
  Polygon,
  ProductInstance,
  SculptedBench,
  Vec2,
  Wall,
  WallFeature,
} from '../shared/types';
import {
  activeCageOf,
  cagePickOf,
  frameAxes,
  framePoint,
  pickCagePoint,
  pickedCagePoint,
  type SculptLayout,
  sculptLayout,
} from './sculptBench';
import {
  CAGE_COLOR,
  cageEdges,
  cageGroup,
  cageLines,
  cagePoint,
  type F3,
  offsetOf,
  pointFreedom,
  restPoint,
  shiftCagePoints,
} from './lattice';
import { traitsOf } from '../shared/categories';
import {
  apexFromCentre,
  type CeilingField,
  type CeilingNote,
  ceilingPlanMarks,
  everyWallVertical,
  hasSetbacks,
  insidePoint,
  isFlatCeiling,
  outerAt,
  resolveCeiling,
  ridgeFromCentre,
  roomBenchSeats,
  setApexAt,
  setApexFromCentre,
  setCeilingHeight,
  setEaveHeight,
  setRidgeAt,
  setRidgeFromCentre,
  setSetbackAt,
  setSpringingHeight,
  setWallSetback,
  springsInside,
  underAt,
  wallFacePitches,
} from './ceiling';
import {
  doorTop,
  externalHeight,
  glassPaneBase,
  glassPaneTop,
  heaterWallBase,
  heaterWallTop,
  innerWallBase,
  interiorHeight,
  objectHeight,
  openingArchitrave,
  openingSill,
  type PartFigures,
  partSpan,
  polygonBase,
  polygonHeight,
  roomBase,
  setObjectHeight,
  wallBase,
  wallHeight,
} from './heights';
import {
  segPoint,
  wallFeatureBase,
  wallFeatureSpans,
  wallFeatureTop,
  wallSegFrame,
} from './wallFeatures';
import {
  heaterWallBands,
  innerWallSpanBand,
  isPolyRoom,
  polyEdges,
  polyInnerWallBand,
  resolvedOutline,
  wallView,
} from './polyRoom';
import { openingBandLocal } from './glass';
import { benchLegOf } from './roomBench';
import { roomDoors } from './door';
import {
  instanceToWorld,
  rotatePoint,
  toInstanceLocal,
} from './geometry';
import {
  drawGrip,
  GRIP_FILL,
  GRIP_HIT_PX,
  GRIP_SELECTED,
} from './grip';
import type { SecPart } from './section';
import {
  isRoomPart,
  type RoomPart,
} from './roomFeatures';
import type {
  ConnGeom,
  ConnPipeGeom,
} from './connections';
import type { ConnEdit } from './connEdits';
import {
  type PipeMotion,
  snapPipeVertex,
} from './pipes';
import { roomDoor } from './roomDoor';

export interface Pt3 { x: number; y: number; z: number }
/** A height a handle snaps to: mm above the room's floor (above the axis's
 *  `from` once given to a handle), and what it is. */
export interface NamedHeight { z: number; name?: string }
export type HandleKind =
  | 'apex'
  | 'architrave'
  | 'bend'
  | 'bottom'
  | 'cage'
  | 'crown'
  | 'eave'
  | 'height'
  | 'lift'
  | 'move'
  | 'plane'
  | 'ridge'
  | 'run'
  | 'seat'
  | 'setback'
  | 'sill'
  | 'springing'
  | 'top';

/** What the readout calls each figure — a feature's as its panel row does —,
 *  and each height a handle catches. */
export const HANDLE_LABELS = {
  height: 'Height', crown: 'Crown', eave: 'Eave', springing: 'Springing', ridge: 'Ridge from centre',
  apex: 'Apex from centre', setback: 'Setback', pitch: 'Pitch',
  top: 'Height', bottom: 'Height from ground', sill: 'Height from ground', architrave: 'Architrave height', seat: 'Height',
  lift: 'Height from ground', plane: '3D section', bend: 'Bend', run: 'Run height', move: 'Move',
  fromHeight: 'From height', toHeight: 'To height', bendHeight: 'Bend height',
  cage: 'Cage point', cageMoved: 'Moved by', cageAlong: 'Along the bench', cageOut: 'Out from the wall', cageUp: 'Up',
} as const;
/** The view state the cage handles read (sculptBench.ts), for the unit tests:
 *  each test bundle holds its own copy of it. */
export {
  pickCagePoint,
  setCagePick,
} from './sculptBench';
/** What a cage point's gizmo arrow reads, by the axis it is drawn as. */
const CAGE_AXIS_LABELS = { x: HANDLE_LABELS.cageAlong, y: HANDLE_LABELS.cageOut, z: HANDLE_LABELS.cageUp } as const;
export const SNAP_NAMES = {
  paneTop: 'Pane top', openingTop: 'Opening top', doorTop: 'Door top', benchSeat: 'Bench seat',
  eave: 'Eave', springing: 'Springing', crown: 'Crown', underside: 'Underside', follows: 'Follows the ceiling',
  wallTop: 'Wall top', wallBottom: 'Wall bottom',
  level: 'Level', fall: 'At the fall', deg45: '45°', autoRun: 'Automatic height',
  atRest: 'At rest',
} as const;
/** Every English string this module hands to tr() (through a variable, so the
 *  i18n gate cannot see them): test/handles.test.mjs checks each has its DICT entry. */
export const HANDLE_STRINGS: readonly string[] = [...new Set([...Object.values(HANDLE_LABELS), ...Object.values(SNAP_NAMES)])];

/** A handle's vertical axis: the figure it shows and sets, on the object `T` it
 *  works on (a room, a part, a drawn wall, an extruded shape). */
export interface HeightAxis<T = ProductInstance> {
  label: string;
  /** mm above `from`. */
  value: number;
  /** The height the figure is measured from, above its object's zero (a room's
   *  floor, else the ground): a partition's, a heater wall's, a drawn wall's or an
   *  extruded shape's base, under its Height. Absent: the zero itself. */
  from?: number;
  set(obj: T, v: number): CeilingNote | null;
  /** The other heights it snaps to, above `from` (never its own, nor what moves
   *  with it). */
  snaps: NamedHeight[];
  /** The room's Height moves: the room is re-laid after the write, as its panel row does. */
  regenerate?: boolean;
  /** The direction (unit, drawing coordinates) the figure grows along in 3D: a
   *  section plane's normal. Absent: straight up. */
  along?: Pt3;
}

/** A handle's plan axis: the point it stands on, moved by a world delta. */
export interface PlanAxis<T = ProductInstance> {
  /** Moves the point by `delta` (world mm) from where it stood at the press;
   *  `snapMm` is the grip's snap reach (0 = free); `along`, in a section view,
   *  the line (world, unit) the move is held to. */
  set(obj: T, delta: Vec2, snapMm: number, along?: Vec2): CeilingNote | null;
  /** The plan direction it moves the figure along (world, unit): in a section
   *  it slides only while that runs within 60° of the view's line. Absent: any. */
  axis?: Vec2;
  /** What the readout says while it moves. */
  readout(obj: T): { label: string; values: number[] } | null;
  /** What it takes when typed once it has moved in plan (a plan-only handle: at
   *  once): one figure — a flat edge's setback, a ridge's offset from the centre
   *  — or two, an apex's x, y from the centre. */
  figure?: { label: string; values: number[]; set(obj: T, v: readonly number[]): CeilingNote | null };
}

export interface Handle<T = ProductInstance> {
  /** Stable while its object keeps its shape: the drag finds its handle again by it. */
  key: string;
  kind: HandleKind;
  /** The height it stands at (mm above the ground). */
  z: number;
  /** 3D: the candidate points (world plan) — the one nearest the eye is drawn. */
  points: Vec2[];
  /** A section: the figure's lines at `z` (a point is a line of two equal ends)… */
  lines?: Array<[Vec2, Vec2]>;
  /** …or the level area it stands over (the middle of what the view shows of it). */
  area?: Vec2[];
  height?: HeightAxis<T>;
  plan?: PlanAxis<T>;
  /** Handles sharing a stack (a cage's points) that stand together in a section
   *  view show only the one nearest the viewer (`handlesInView`). */
  stack?: string;
  /** Drawn lit: one of the points a drag of the picked one moves with it. */
  lit?: boolean;
  /** What a press on it marks besides its work: a cage point, the one the 3D
   *  move gizmo then stands on. */
  pick?: () => void;
}
export type RoomHandle = Handle<ProductInstance>;

/** The height (above the ground) a handle's figure counts from: what its guide
 *  rises from and a section's heights are measured above. */
export function figureZero(hd: Pick<Handle<never>, 'z' | 'height'>): number {
  return hd.z - (hd.height?.value ?? 0);
}

// ---- Small geometry ----------------------------------------------------------

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
const mul = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });
const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const len = (a: Vec2): number => Math.hypot(a.x, a.y);

/** The stretches of the infinite line through `o` along unit `dir` inside a
 *  simple ring (even–odd over its crossings). */
export function lineStretches(ring: readonly Vec2[], o: Vec2, dir: Vec2): Array<[Vec2, Vec2]> {
  const nrm = { x: -dir.y, y: dir.x }, ts: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i], q = ring[(i + 1) % ring.length];
    const fp = dot(nrm, sub(p, o)), fq = dot(nrm, sub(q, o));
    if ((fp > 0) === (fq > 0)) continue;
    const s = fp / (fp - fq);
    ts.push(dot(dir, sub(lerp(p, q, s), o)));
  }
  ts.sort((a, b) => a - b);
  const out: Array<[Vec2, Vec2]> = [];
  for (let k = 0; k + 1 < ts.length; k += 2) if (ts[k + 1] - ts[k] > 1e-6) out.push([add(o, mul(dir, ts[k])), add(o, mul(dir, ts[k + 1]))]);
  return out;
}

/** The ring's edges (as given) lying on the level `z` of `at` along their length —
 *  read at their ends and between: a gable's end wall has both corners on the
 *  eave and its middle at the ridge. */
function edgesAt(ring: readonly Vec2[], at: (p: Vec2) => number, z: number): Array<[Vec2, Vec2]> {
  const on = (p: Vec2): boolean => Math.abs(at(p) - z) < 0.5;
  const out: Array<[Vec2, Vec2]> = [];
  ring.forEach((p, i) => {
    const q = ring[(i + 1) % ring.length];
    if (len(sub(q, p)) > 1e-6 && [0, 0.25, 0.5, 0.75, 1].every((s) => on(lerp(p, q, s)))) out.push([p, q]);
  });
  return out;
}

/** The midpoints a 3D handle may stand at along lines: of the lines a tenth of
 *  the longest or longer (a rounded corner's short chords offer none). */
function midpoints(lines: ReadonlyArray<[Vec2, Vec2]>): Vec2[] {
  const L = Math.max(0, ...lines.map(([a, b]) => len(sub(b, a))));
  return lines.filter(([a, b]) => len(sub(b, a)) >= L / 10).map(([a, b]) => lerp(a, b, 0.5));
}

// ---- The room's handles ------------------------------------------------------

/** The room's outline in its own frame: a poly-room's (rounded corners
 *  followed), a legacy rectangle's box. */
function outlineLocal(inst: ProductInstance): Vec2[] {
  if (isPolyRoom(inst)) return resolvedOutline(inst, 0);
  const hw = inst.width / 2, hd = inst.depth / 2;
  return [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }];
}

/** The heights of a room a handle can catch, mm above its floor: its panes',
 *  openings' and doors' tops, its benches' seats, and a shaped ceiling's
 *  eave, springing and crown — `except`'s own left out (they move with its
 *  handles). Each (height, name) once. */
export function roomSnapHeights(inst: ProductInstance, cf: CeilingField | null, except?: SecPart): NamedHeight[] {
  const out: NamedHeight[] = [];
  const push = (z: number, name: string): void => {
    if (Number.isFinite(z) && !out.some((h) => h.name === name && Math.abs(h.z - z) < 1e-6)) out.push({ z, name });
  };
  const own = (kind: SecPart['kind'], id: string | undefined): boolean => except?.kind === kind && except.id === id;
  for (const g of inst.glass ?? []) if (!own('pane', g.id)) push(glassPaneTop(inst, g), SNAP_NAMES.paneTop);
  for (const o of inst.openings ?? []) if (!own('opening', o.id)) push(openingArchitrave(inst, o), SNAP_NAMES.openingTop);
  for (const d of roomDoors(inst)) if (!own('door', d.id)) push(doorTop(inst, d), SNAP_NAMES.doorTop);
  for (const s of roomBenchSeats(inst)) if (!own('bench', s.id)) push(s.seat, SNAP_NAMES.benchSeat);
  if (cf && !cf.flat) {
    if (!springsInside(cf.shape) && !everyWallVertical(cf)) push(cf.eave, SNAP_NAMES.eave);
    if (cf.springing != null && !everyWallVertical(cf)) push(cf.springing, SNAP_NAMES.springing);
    push(cf.crown, SNAP_NAMES.crown);
  }
  return out;
}

/** The pitch the readout shows while an eave or a crown moves — the Ceiling
 *  card's Pitch row: a slope's or a gable's picked side, a pyramid's or a
 *  truncated pyramid's shallowest straight-wall face. Null for the others. */
export function handlePitch(inst: ProductInstance): number | null {
  if (isFlatCeiling(inst)) return null;
  const cf = resolveCeiling(inst);
  if (cf.flat) return null;
  if (cf.shape === 'slope' || cf.shape === 'gable') return cf.facePitchDeg[0] ?? null;
  if (cf.shape === 'frustum' || cf.shape === 'pyramid') {
    const f = wallFacePitches(cf);
    return f.length ? Math.min(...f) : null;
  }
  return null;
}

/** Every handle of a selected room (sauna / steam room): its Height while its
 *  ceiling is flat; else the crown, the eave or the springing, a gable's ridge
 *  and a pyramid's / dome's apex (each ONE handle with the crown: the first move
 *  decides), a truncated pyramid's or a cove's flat edges. None for a ceiling drawn flat
 *  because its shape no longer fits (its panel says why). */
export function roomHandles(inst: ProductInstance): RoomHandle[] {
  if (!inst.autoBenches) return [];
  const base = roomBase(inst);
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const toLocal = (d: Vec2): Vec2 => rotatePoint(d, -inst.rotation);
  const toWorldDir = (d: Vec2): Vec2 => rotatePoint(d, inst.rotation);
  const lines = (ls: ReadonlyArray<[Vec2, Vec2]>): Array<[Vec2, Vec2]> => ls.map(([a, b]) => [W(a), W(b)]);
  if (isFlatCeiling(inst)) {
    const ring = outlineLocal(inst), H = externalHeight(inst);
    return [{
      key: 'height', kind: 'height', z: base + H, points: [W(insidePoint(ring))], area: ring.map(W),
      height: { label: HANDLE_LABELS.height, value: H, set: setCeilingHeight, snaps: roomSnapHeights(inst, null), regenerate: true },
    }];
  }
  const cf = resolveCeiling(inst);
  if (cf.flat) return [];
  const heights = roomSnapHeights(inst, cf);
  const but = (name: string): NamedHeight[] => heights.filter((h) => h.name !== name);
  const crownAt = cf.crownAt ?? insidePoint(cf.innerRing);
  const crown: HeightAxis = { label: HANDLE_LABELS.crown, value: cf.crown, set: setCeilingHeight, snaps: but(SNAP_NAMES.crown), regenerate: true };
  const out: RoomHandle[] = [];
  const crownZ = base + cf.crown;
  const ax = cf.uAxis;
  // The crown's own handle, where no ridge or apex shares its point.
  if (cf.shape === 'slope') {
    const ls = lines(edgesAt(cf.outerRing, (p) => outerAt(cf, p), cf.crown));
    out.push({ key: 'crown', kind: 'crown', z: crownZ, points: [W(crownAt)], lines: ls, height: crown });
  } else if (hasSetbacks(cf.shape)) {
    out.push({ key: 'crown', kind: 'crown', z: crownZ, points: [W(crownAt)], area: (cf.flatRing ?? cf.innerRing).map(W), height: crown });
  } else if (cf.shape === 'barrel' && ax) {
    const o = add(ax.a, mul(ax.n, (ax.u0In + ax.u1In) / 2)), dir = { x: -ax.n.y, y: ax.n.x };
    out.push({ key: 'crown', kind: 'crown', z: crownZ, points: [W(crownAt)], lines: lines(lineStretches(cf.outerRing, o, dir)), height: crown });
  } else if (cf.shape === 'gable' && ax && cf.ridgeOut != null) {
    const grip = ceilingPlanMarks(inst).grips.find((g) => g.kind === 'ridge');
    const o = add(ax.a, mul(ax.n, cf.ridgeOut)), dir = { x: -ax.n.y, y: ax.n.x };
    out.push({
      key: 'ridge', kind: 'ridge', z: crownZ, points: [W(crownAt)], lines: lines(lineStretches(cf.outerRing, o, dir)), height: crown,
      plan: grip ? {
        axis: toWorldDir(ax.n),
        set: (r, d, snapMm) => {
          const why = setRidgeAt(r, add(grip.at, toLocal(d)));
          const off = ridgeFromCentre(resolveCeiling(r));
          if (snapMm > 0 && off != null && off !== 0 && Math.abs(off) <= snapMm) return setRidgeFromCentre(r, 0);
          return why;
        },
        readout: (r) => { const v = ridgeFromCentre(resolveCeiling(r)); return v == null ? null : { label: HANDLE_LABELS.ridge, values: [v] }; },
        figure: { label: HANDLE_LABELS.ridge, values: [ridgeFromCentre(cf) ?? 0], set: (r, [v]) => setRidgeFromCentre(r, v) },
      } : undefined,
    });
  } else if ((cf.shape === 'pyramid' || cf.shape === 'dome') && cf.apex) {
    const A0 = cf.apex, from = apexFromCentre(cf);
    out.push({
      key: 'apex', kind: 'apex', z: crownZ, points: [W(crownAt)], lines: [[W(crownAt), W(crownAt)]], height: crown,
      plan: {
        set: (r, d, snapMm) => setApexAt(r, add(A0, toLocal(d)), snapMm),
        readout: (r) => { const v = apexFromCentre(resolveCeiling(r)); return v ? { label: HANDLE_LABELS.apex, values: [v.x, v.y] } : null; },
        figure: from ? { label: HANDLE_LABELS.apex, values: [from.x, from.y], set: (r, [x, y]) => setApexFromCentre(r, x, y) } : undefined,
      },
    });
  }
  // The eave (an outside figure) or the springing (an inside one).
  if (springsInside(cf.shape)) {
    if (cf.springing != null) {
      const ls = lines(edgesAt(cf.innerRing, (p) => underAt(cf, p), cf.springing));
      if (ls.length) {
        out.push({
          key: 'springing', kind: 'springing', z: base + cf.springing, points: midpoints(ls), lines: ls,
          height: { label: HANDLE_LABELS.springing, value: cf.springing, set: setSpringingHeight, snaps: but(SNAP_NAMES.springing) },
        });
      }
    }
  } else if (!everyWallVertical(cf)) {
    const ls = lines(edgesAt(cf.outerRing, (p) => outerAt(cf, p), cf.eave));
    if (ls.length) {
      out.push({
        key: 'eave', kind: 'eave', z: base + cf.eave, points: midpoints(ls), lines: ls,
        height: { label: HANDLE_LABELS.eave, value: cf.eave, set: setEaveHeight, snaps: but(SNAP_NAMES.eave) },
      });
    }
  }
  // A truncated pyramid's or a cove's flat edges: plan only, each its run's setback.
  if (hasSetbacks(cf.shape) && cf.flatRing && cf.setbacks) {
    const F = cf.flatRing, n = F.length, edges = polyEdges(wallView(inst));
    for (const g of ceilingPlanMarks(inst).grips) {
      if (g.kind !== 'setback' || !g.walls?.length) continue;
      const w = g.walls[0], k1 = (g.walls[g.walls.length - 1] + 1) % n;
      const at0 = g.at, e = edges[w];
      out.push({
        key: `setback:${w}`, kind: 'setback', z: crownZ, points: [W(at0)], lines: [[W(F[w]), W(F[k1])]],
        plan: {
          axis: e ? toWorldDir(e.normal) : undefined,
          set: (r, d, snapMm) => setSetbackAt(r, w, add(at0, toLocal(d)), snapMm),
          readout: (r) => { const s = resolveCeiling(r).setbacks?.[w]; return s == null ? null : { label: HANDLE_LABELS.setback, values: [s] }; },
          figure: { label: HANDLE_LABELS.setback, values: [cf.setbacks[w] ?? 0], set: (r, [v]) => setWallSetback(r, w, v) },
        },
      });
    }
  }
  return out;
}

// ---- A room feature's handles --------------------------------------------------

/** The line a band's handles stand on — its middle, end to end — for a band whose
 *  ends are its edges 3→0 and 1→2 (`'wall'`: a pane's, an opening's, a doorway's,
 *  a heater wall's) or 0→1 and 2→3 (`'partition'`: a partition's, a stretch of it). */
function bandMid(q: readonly Vec2[] | null | undefined, ends: 'wall' | 'partition'): [Vec2, Vec2] | null {
  if (!q || q.length < 4) return null;
  return ends === 'wall' ? [lerp(q[3], q[0], 0.5), lerp(q[1], q[2], 0.5)] : [lerp(q[0], q[1], 0.5), lerp(q[3], q[2], 0.5)];
}

/** The run a room's pane or opening stands on (local): its band's middle, end to
 *  end — on its partition's band when it is in one. */
function paneRunLocal(inst: ProductInstance, seg: GlassSegment): [Vec2, Vec2] | null {
  const iw = seg.innerWallId != null ? (inst.innerWalls ?? []).find((w) => w.id === seg.innerWallId) : undefined;
  return seg.innerWallId != null
    ? bandMid(iw ? innerWallSpanBand(inst, iw, seg.start, seg.end) : null, 'partition')
    : bandMid(openingBandLocal(inst, seg), 'wall');
}

/** Where a room's pane or opening rising meets the ceiling: the lowest underside
 *  over its run (mm above the room's floor). Null for one not found, or a room
 *  that is not a polygon. */
export function featureCeiling(inst: ProductInstance, part: SecPart): number | null {
  if (!inst.autoBenches || !isPolyRoom(inst) || (part.kind !== 'pane' && part.kind !== 'opening')) return null;
  const seg = (part.kind === 'pane' ? inst.glass : inst.openings)?.find((g) => g.id === part.id);
  const line = seg ? paneRunLocal(inst, seg) : null;
  if (!line) return null;
  const cf = isFlatCeiling(inst) ? null : resolveCeiling(inst);
  if (!cf || cf.flat) return interiorHeight(inst);
  let low = Infinity;
  for (let k = 0; k <= 8; k++) low = Math.min(low, underAt(cf, lerp(line[0], line[1], k / 8)));
  return low;
}

/** A top set to `v` stops at the ceiling `cap` — or where it already stood, when
 *  that is higher (cut by a shaped ceiling): it never rises into the ceiling. */
const underCap = (v: number, cap: number | null, now: number): number => (cap == null ? v : Math.min(v, Math.max(cap, now)));

/** What one of a room's features' handles are built from (`featureHandles`). */
interface FeatureHandleCtx {
  inst: ProductInstance;
  part: SecPart;
  id: string;
  W: (p: Vec2) => Vec2;
  base: number;
  shaped: boolean;
  /** The underside over a point (local), mm above the floor. */
  under: (p: Vec2) => number;
  /** The room's heights (and `extra`), above `from`. */
  above: (from: number, extra?: NamedHeight[]) => NamedHeight[];
  /** A handle at `z` above the floor on the run `line` (local) — on its middle
   *  alone when `atMiddle` (a top that is not level along it). */
  add: (kind: HandleKind, line: [Vec2, Vec2], z: number, height: HeightAxis, atMiddle?: boolean) => void;
  out: RoomHandle[];
}

const midOf = (line: [Vec2, Vec2]): Vec2 => lerp(line[0], line[1], 0.5);
const lowered = (v: number, top: number): number => Math.max(0, Math.min(v, top - 1));

/** A pane's top and bottom, an opening's sill and architrave. */
function paneHandles({ inst, part, id, under, above, add }: FeatureHandleCtx): void {
  const listOf = (r: ProductInstance): GlassSegment[] | undefined => (part.kind === 'pane' ? r.glass : r.openings);
  const find = (r: ProductInstance): GlassSegment | undefined => listOf(r)?.find((g) => g.id === id);
  const seg = find(inst);
  if (!seg) return;
  const line = paneRunLocal(inst, seg);
  if (!line) return;
  const underside: NamedHeight = { z: under(midOf(line)), name: SNAP_NAMES.underside };
  const cap = featureCeiling(inst, part);
  if (part.kind === 'pane') {
    const top = glassPaneTop(inst, seg), b0 = glassPaneBase(seg);
    add('top', line, top, {
      label: HANDLE_LABELS.top, value: top, snaps: above(0, [underside]),
      set: (r, v) => { const g = find(r); if (g) g.height = Math.max(glassPaneBase(g) + 1, underCap(v, cap, glassPaneTop(r, g))); return null; },
    });
    add('bottom', line, b0, {
      label: HANDLE_LABELS.bottom, value: b0, snaps: above(0),
      set: (r, v) => {
        const g = find(r);
        if (!g) return null;
        const b = lowered(v, glassPaneTop(r, g));
        if (b > 0) g.heightFromGround = b; else delete g.heightFromGround;
        return null;
      },
    });
  } else {
    const sill = openingSill(seg), arch = openingArchitrave(inst, seg);
    add('sill', line, sill, {
      label: HANDLE_LABELS.sill, value: sill, snaps: above(0),
      set: (r, v) => { const o = find(r); if (o) o.heightFromGround = lowered(v, openingArchitrave(r, o)); return null; },
    });
    add('architrave', line, arch, {
      label: HANDLE_LABELS.architrave, value: arch, snaps: above(0, [underside]),
      set: (r, v) => { const o = find(r); if (o) o.architraveHeight = Math.max(openingSill(o) + 1, underCap(v, cap, openingArchitrave(r, o))); return null; },
    });
  }
}

/** A door's top. */
function doorHandles({ inst, id, under, above, add }: FeatureHandleCtx): void {
  const door = roomDoors(inst).find((d) => d.id === id);
  if (!door) return;
  const rd = roomDoor(inst, door);
  const line = rd ? bandMid(rd.gap, rd.onPartition ? 'partition' : 'wall') : null;
  if (!line) return;
  const top = doorTop(inst, door);
  add('top', line, top, {
    label: HANDLE_LABELS.top, value: top, snaps: above(0, [{ z: under(midOf(line)), name: SNAP_NAMES.underside }]),
    set: (r, v) => { const d = roomDoors(r).find((x) => x.id === id); if (d) d.height = Math.max(1, Math.round(v)); return null; },
  });
}

/** A partition's top and bottom; its top dropped on the underside follows the
 *  ceiling again. */
function partitionHandles({ inst, id, shaped, under, above, add }: FeatureHandleCtx): void {
  const find = (r: ProductInstance) => (r.innerWalls ?? []).find((w) => w.id === id);
  const iw = find(inst);
  const line = iw ? bandMid(polyInnerWallBand(inst, iw), 'partition') : null;
  if (!iw || !line) return;
  const b0 = innerWallBase(iw), u = under(midOf(line));
  // With no height of its own its top is the underside (`innerWallTop` /
  // `innerWallTopAt`), and its figure the height from its base up to it.
  const top = iw.height ?? Math.max(0, u - b0);
  add('top', line, b0 + top, {
    label: HANDLE_LABELS.top, value: top, from: b0, snaps: above(b0, [{ z: u, name: SNAP_NAMES.follows }]),
    set: (r, v) => {
      const w = find(r);
      if (!w) return null;
      if (Math.abs(v - (u - innerWallBase(w))) < 0.5) delete w.height;
      else w.height = Math.max(0, Math.round(v));
      return null;
    },
  }, shaped && iw.height == null);
  add('bottom', line, b0, {
    label: HANDLE_LABELS.bottom, value: b0, snaps: above(0),
    set: (r, v) => {
      const w = find(r);
      if (!w) return null;
      const t = w.height != null ? innerWallBase(w) + Math.max(0, w.height) : u;
      const b = lowered(Math.round(v), t);
      if (w.height != null) w.height = Math.max(0, t - b);
      if (b > 0 || w.heightFromGround != null) w.heightFromGround = b;
      return null;
    },
  });
}

/** A heater wall's top and bottom. */
function heaterWallHandles({ inst, id, under, above, add }: FeatureHandleCtx): void {
  const find = (r: ProductInstance) => (r.heaterWalls ?? []).find((w) => w.id === id);
  const hb = heaterWallBands(inst).find((b) => b.wall.id === id);
  const line = hb ? bandMid(hb.poly, 'wall') : null;
  if (!hb || !line) return;
  const b0 = heaterWallBase(hb.wall), t0 = heaterWallTop(hb.wall);
  add('top', line, t0, {
    label: HANDLE_LABELS.top, value: t0 - b0, from: b0, snaps: above(b0, [{ z: under(midOf(line)), name: SNAP_NAMES.underside }]),
    set: (r, v) => { const w = find(r); if (w) w.height = Math.max(0, Math.round(v)); return null; },
  });
  add('bottom', line, b0, {
    label: HANDLE_LABELS.bottom, value: b0, snaps: above(0),
    set: (r, v) => {
      const w = find(r);
      if (!w) return null;
      const t = heaterWallTop(w), b = lowered(Math.round(v), t);
      w.height = Math.max(0, t - b);
      w.heightFromGround = b;
      return null;
    },
  });
}

/** A bench's seat — none for a steam bench swept from a profile (its seat is
 *  the profile's). */
function benchHandles({ inst, id, W, base, above, out }: FeatureHandleCtx): void {
  const find = (r: ProductInstance) => benchLegOf(r, id);
  const leg = find(inst), seat = roomBenchSeats(inst).find((s) => s.id === id);
  if (!leg || leg.profile || !seat || seat.poly.length < 3) return;
  out.push({
    key: `bench:${id}:seat`, kind: 'seat', z: base + seat.seat, points: [W(insidePoint(seat.poly))], area: seat.poly.map(W),
    height: {
      label: HANDLE_LABELS.seat, value: seat.seat, snaps: above(0),
      set: (r, v) => { const b = find(r); if (b) b.height = Math.max(0, Math.round(v)); return null; },
    },
  });
}

/** The sculpted bench a fixture id names on a room, its layout and the cage its
 *  handles edit (the active one), or null. */
function sculptedCage(inst: ProductInstance, id: string): { sb: SculptedBench; lay: SculptLayout; cage: Lattice } | null {
  const sb = inst.sculptedBenches?.find((b) => b.id === id);
  const lay = sb ? sculptLayout(inst, sb) : null;
  const cage = sb ? activeCageOf(sb) : null;
  return sb && lay && cage && cage.enabled !== false ? { sb, lay, cage } : null;
}

/** A sculpted bench's handles: one on each point of its active cage that may
 *  move (`pointFreedom`), where the cage puts it. Its height sets the point's up
 *  offset (snapping back to where it rests); a move in plan, its along and out
 *  offsets, read off the run's frame (`locate`) — a back point, whose out is
 *  locked, slides along the run alone. The points stack: a section view shows
 *  the ones nearest it. */
function sculptCageHandles({ inst, id, W, base, above, out }: FeatureHandleCtx): void {
  const sc = sculptedCage(inst, id);
  if (!sc) return;
  const { sb, lay, cage } = sc;
  const ext = lay.extent;
  const find = (r: ProductInstance): Lattice | null => r.sculptedBenches?.find((b) => b.id === sb.id)?.lattices?.find((c) => c.id === cage.id) ?? null;
  const rot = (v: Vec2): Vec2 => rotatePoint(v, inst.rotation || 0);
  const pick = cagePickOf(sb.id), picked = pickedCagePoint(sb, ext);
  const litKeys = new Set(picked && pick !== 'point' ? cageGroup(cage, pick, picked.i, picked.j, picked.k).map((g) => g.join(',')) : []);
  for (let i = 0; i < cage.nx; i++) for (let j = 0; j < cage.ny; j++) for (let k = 0; k < cage.nz; k++) {
    const free = pointFreedom(cage, ext, i, j, k);
    if (!free) continue;
    const rest = restPoint(cage, i, j, k), now = cagePoint(cage, i, j, k);
    const p = framePoint(lay, now), at = W(p);
    const offsetNow = (c: Lattice): F3 => offsetOf(c, i, j, k);
    // The points it moves with it, each from where it stood at the press.
    const group = cageGroup(cage, pick, i, j, k);
    const from = group.map(([a, b, c]) => offsetOf(cage, a, b, c));
    const shift = (r: ProductInstance, d: [number | null, number | null, number | null]): void => { const c = find(r); if (c) shiftCagePoints(c, ext, group, from, d); };
    const plan = free.along || free.out;
    const d = lay.run.dir(now[0], now[1]);
    out.push({
      key: `fixture:${sb.id}:cage:${cage.id}:${i},${j},${k}`, kind: 'cage', z: base + p.z, points: [at], lines: [[at, at]],
      stack: `${sb.id}:${cage.id}`,
      ...(litKeys.has(`${i},${j},${k}`) ? { lit: true } : {}),
      pick: () => pickCagePoint(sb.id, { cage: cage.id, i, j, k }),
      height: free.up ? {
        label: HANDLE_LABELS.cage, value: p.z, snaps: above(0, [{ z: rest[2] + lay.lift, name: SNAP_NAMES.atRest }]),
        set: (r, v) => { shift(r, [null, null, v - lay.lift - rest[2] - from[0][2]]); return null; },
      } : undefined,
      plan: plan ? {
        axis: free.out ? (free.along ? undefined : rot({ x: -d.y, y: d.x })) : rot(d),
        set: (r, delta) => {
          const q = lay.run.locate(toInstanceLocal(r, add(at, delta)), { along: now[0], out: now[1] });
          shift(r, [q.along - now[0], free.out ? q.out - now[1] : 0, null]);
          return null;
        },
        readout: (r) => { const c = find(r); return c ? { label: HANDLE_LABELS.cageMoved, values: offsetNow(c).slice(0, 2) } : null; },
      } : undefined,
    });
  }
}

/** The run's three directions at a picked cage point, as the 3D move gizmo's
 *  arrows draw them (world: along the bench, out from its faces, up), and the
 *  handle a press on each works: a figure sliding along it, 0 at the press, that
 *  moves the picked point — and the points its pick moves with it — as far in the
 *  bench's frame. Null when no point of the selected bench's active cage is
 *  picked. */
export function cagePointGizmo(inst: ProductInstance, id: string): { origin: Pt3; dirs: Record<'x' | 'y' | 'z', Pt3>; handle(axis: 'x' | 'y' | 'z'): RoomHandle } | null {
  const sc = sculptedCage(inst, id);
  if (!sc || !inst.autoBenches || !isPolyRoom(inst)) return null;
  const { sb, lay, cage } = sc;
  const ext = lay.extent, p = pickedCagePoint(sb, ext);
  if (!p) return null;
  const free = pointFreedom(cage, ext, p.i, p.j, p.k)!;
  const now = cagePoint(cage, p.i, p.j, p.k), fp = framePoint(lay, now);
  const base = roomBase(inst), w = instanceToWorld(inst, fp);
  const rot = (v: Vec2): Pt3 => { const q = rotatePoint(v, inst.rotation || 0); return { x: q.x, y: q.y, z: 0 }; };
  const ax = frameAxes(lay, now);
  const all = { x: rot(ax.along), y: rot(ax.out), z: { x: 0, y: 0, z: 1 } };
  const dirs = Object.fromEntries((['x', 'y', 'z'] as const).filter((a) => (a === 'x' ? free.along : a === 'y' ? free.out : free.up)).map((a) => [a, all[a]])) as Record<'x' | 'y' | 'z', Pt3>;
  const group = cageGroup(cage, cagePickOf(sb.id), p.i, p.j, p.k);
  const from = group.map(([a, b, c]) => offsetOf(cage, a, b, c));
  const find = (r: ProductInstance): Lattice | null => r.sculptedBenches?.find((b) => b.id === sb.id)?.lattices?.find((c) => c.id === cage.id) ?? null;
  return {
    origin: { x: w.x, y: w.y, z: base + fp.z }, dirs,
    handle: (axis) => ({
      key: `fixture:${sb.id}:cage:${cage.id}:${p.i},${p.j},${p.k}:${axis}`, kind: 'cage', z: base + fp.z, points: [w],
      height: {
        label: CAGE_AXIS_LABELS[axis], value: 0, snaps: [], along: dirs[axis],
        set: (r, v) => {
          const c = find(r);
          if (c) shiftCagePoints(c, ext, group, from, axis === 'x' ? [v, null, null] : axis === 'y' ? [null, v, null] : [null, null, v]);
          return null;
        },
      },
    }),
  };
}

/** A sculpted bench's active cage as 3D lines (world plan, heights above the
 *  ground): what its handles stand on, drawn with them. Empty for any other part. */
export function featureGuides(inst: ProductInstance, part: SecPart): Pt3[][] {
  if (part.kind !== 'fixture' || !inst.autoBenches || !isPolyRoom(inst)) return [];
  const sc = sculptedCage(inst, part.id);
  if (!sc) return [];
  const base = roomBase(inst);
  return cageLines(sc.cage).map((line) => line.map((f) => {
    const p = framePoint(sc.lay, f), w = instanceToWorld(inst, p);
    return { x: w.x, y: w.y, z: base + p.z };
  }));
}

/** A sculpted bench's other cages (all but the active one) as faint boxes: each
 *  one's twelve edges as they stand (world plan, heights above the ground). */
export function featureFaintGuides(inst: ProductInstance, part: SecPart): Pt3[][] {
  if (part.kind !== 'fixture' || !inst.autoBenches || !isPolyRoom(inst)) return [];
  const sb = inst.sculptedBenches?.find((b) => b.id === part.id);
  const lay = sb ? sculptLayout(inst, sb) : null;
  if (!sb || !lay) return [];
  const active = activeCageOf(sb), base = roomBase(inst);
  return (sb.lattices ?? []).filter((c) => c !== active).flatMap((c) => cageEdges(c).map((line) => line.map((f) => {
    const p = framePoint(lay, f), w = instanceToWorld(inst, p);
    return { x: w.x, y: w.y, z: base + p.z };
  })));
}

/** Each room part's handles: a support has none, a fixture only a sculpted
 *  bench's cage points. */
const FEATURE_HANDLES: Record<RoomPart, ((c: FeatureHandleCtx) => void) | null> = {
  bench: benchHandles,
  door: doorHandles,
  fixture: sculptCageHandles,
  heaterWall: heaterWallHandles,
  opening: paneHandles,
  pane: paneHandles,
  partition: partitionHandles,
  support: null,
};

/** The handles of one of a room's features (`FEATURE_HANDLES`), each on the
 *  middle of its run. A bottom handle moves the bottom alone, the top staying
 *  where it is. Each figure is its panel row's; it snaps to the room's heights
 *  (`roomSnapHeights`) and a top to the underside over it. Poly rooms only;
 *  none for a feature not found, nor for a part that has none (decided before
 *  the ceiling is read). */
export function featureHandles(inst: ProductInstance, part: SecPart): RoomHandle[] {
  const make = isRoomPart(part.kind) ? FEATURE_HANDLES[part.kind] : null;
  if (!make || !inst.autoBenches || !isPolyRoom(inst)) return [];
  const base = roomBase(inst);
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const cf = isFlatCeiling(inst) ? null : resolveCeiling(inst);
  const shaped = !!cf && !cf.flat;
  const heights = roomSnapHeights(inst, cf, part);
  const id = part.id;
  const out: RoomHandle[] = [];
  make({
    inst, part, id, W, base, shaped, out,
    under: (p) => (cf && shaped ? underAt(cf, p) : interiorHeight(inst)),
    above: (from, extra = []) => [...heights, ...extra].map((h) => ({ z: h.z - from, name: h.name })),
    add: (kind, line, z, height, atMiddle = false) => {
      const m = W(midOf(line));
      out.push({ key: `${part.kind}:${id}:${kind}`, kind, z: base + z, points: [m], lines: [atMiddle ? [m, m] : [W(line[0]), W(line[1])]], height });
    },
  });
  return out;
}

// ---- A part's, a node object's, a drawn wall's, an extruded shape's handles ----

/** The heights of the room a part or a node object stands in, above the GROUND
 *  (its floor lifted by the room's own base) — what a handle on it catches — and,
 *  at `at` (world), the underside there. */
export function hostHeights(host: ProductInstance | null | undefined, at?: Vec2): NamedHeight[] {
  if (!host?.autoBenches) return [];
  const cf = isFlatCeiling(host) ? null : resolveCeiling(host);
  const b = roomBase(host);
  const out = roomSnapHeights(host, cf).map((h) => ({ z: b + h.z, name: h.name }));
  if (at) out.push({ z: b + (cf && !cf.flat ? underAt(cf, toInstanceLocal(host, at)) : interiorHeight(host)), name: SNAP_NAMES.underside });
  return out;
}

/** A node object's body, one middle line per segment (its node line is the back
 *  edge, the body `depth` along (dy, −dx)). */
function nodeRunLines(inst: ProductInstance): Array<[Vec2, Vec2]> {
  const nodes = inst.nodes ?? [], h = inst.depth / 2, out: Array<[Vec2, Vec2]> = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1], l = len(sub(b, a));
    if (l < 1e-6) continue;
    const o = { x: ((b.y - a.y) / l) * h, y: (-(b.x - a.x) / l) * h };
    out.push([add(a, o), add(b, o)]);
  }
  return out;
}

/** The handles of a part or a node object (never a room's): a heater's or a
 *  shower's LIFT — its Height from ground, the body moving whole, on its bottom
 *  edges (where `partSpan` puts it, `template` its catalogue product) —, a salt
 *  wall's or a standalone sauna or heated bench's top — its Height, on the middle
 *  of its run. Both snap to the heights of the room it stands in (`host`), above
 *  the ground; a top to the underside over it too. */
export function partHandles(inst: ProductInstance, host?: ProductInstance | null, template?: PartFigures | null): RoomHandle[] {
  if (inst.autoBenches) return [];
  if (inst.nodes && inst.nodes.length >= 2) {
    const ls = traitsOf(inst.category).run ? nodeRunLines(inst) : [];
    if (!ls.length) return [];
    const top = objectHeight(inst);
    const long = ls.reduce((a, l) => (len(sub(l[1], l[0])) > len(sub(a[1], a[0])) ? l : a));
    return [{
      key: 'top', kind: 'top', z: top, points: midpoints(ls), lines: ls,
      height: {
        label: HANDLE_LABELS.top, value: top, snaps: hostHeights(host, lerp(long[0], long[1], 0.5)),
        set: (i, v) => { setObjectHeight(i, v); return null; },
      },
    }];
  }
  if (!traitsOf(inst.category).floatsBody) return [];
  const [b0] = partSpan(inst, template);
  const hw = inst.width / 2, hd = inst.depth / 2;
  const ring = [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }].map((p) => instanceToWorld(inst, p));
  const ls = ring.map((p, i): [Vec2, Vec2] => [p, ring[(i + 1) % ring.length]]);
  return [{
    key: 'lift', kind: 'lift', z: b0, points: midpoints(ls), lines: ls,
    height: {
      label: HANDLE_LABELS.lift, value: b0, snaps: hostHeights(host),
      set: (i, v) => { i.heightFromGround = Math.round(v); return null; },
    },
  }];
}

/** A drawn wall's middle line per segment (between its two faces). */
function wallRunLines(w: Wall): Array<[Vec2, Vec2]> {
  const out: Array<[Vec2, Vec2]> = [];
  for (let i = 0; i + 1 < w.points.length; i++) {
    const fr = wallSegFrame(w, i);
    if (!fr) continue;
    const c = (fr.oL - fr.oR) / 2;
    out.push([segPoint(fr, 0, c), segPoint(fr, fr.len, c)]);
  }
  return out;
}

/** A drawn wall's heights a handle catches, above the ground: its doors', panes'
 *  and openings' tops — `except`'s left out, and with `followers` false the panes
 *  whose top is the wall's own (they move with it). */
function wallSnapHeights(w: Wall, except?: string, followers = true): NamedHeight[] {
  const out: NamedHeight[] = [];
  for (const { f } of wallFeatureSpans(w)) {
    if ((except && f.id === except) || (!followers && f.kind === 'glass' && f.height == null)) continue;
    const name = f.kind === 'door' ? SNAP_NAMES.doorTop : f.kind === 'glass' ? SNAP_NAMES.paneTop : SNAP_NAMES.openingTop;
    const z = wallFeatureTop(w, f);
    if (!out.some((h) => h.name === name && Math.abs(h.z - z) < 1e-6)) out.push({ z, name });
  }
  return out;
}

/** A drawn wall's handles, on the middle of its runs: its top — its Height, from
 *  its base — and, a band wall's, its bottom — its Height from ground, the top
 *  staying where it is (none for a wall 0 mm tall: nothing of it is drawn); or,
 *  with `part` one of its features, that feature's (`wallFeatureHandles`). Each
 *  figure is its panel row's. */
export function wallHandles(w: Wall, part?: SecPart): Array<Handle<Wall>> {
  if (w.points.length < 2) return [];
  if (part) return part.kind === 'wallFeature' && w.style !== 'line' ? wallFeatureHandles(w, part.id) : [];
  const ls = wallRunLines(w), b = wallBase(w), H = wallHeight(w);
  if (!ls.length || H <= 0) return [];
  const pts = midpoints(ls);
  const out: Array<Handle<Wall>> = [{
    key: 'top', kind: 'top', z: b + H, points: pts, lines: ls,
    height: {
      label: HANDLE_LABELS.top, value: H, from: b, snaps: wallSnapHeights(w, undefined, false).map((h) => ({ z: h.z - b, name: h.name })),
      set: (x, v) => { x.height = Math.max(1, v); return null; },
    },
  }];
  if (w.style !== 'line') {
    out.push({
      key: 'bottom', kind: 'bottom', z: b, points: pts, lines: ls,
      height: {
        label: HANDLE_LABELS.bottom, value: b, snaps: [],
        set: (x, v) => {
          const b0 = wallBase(x), t = b0 + wallHeight(x), nb = Math.min(Math.round(v), t - 1);
          if (nb !== b0) { x.height = t - nb; x.heightFromGround = nb; }
          return null;
        },
      },
    });
  }
  return out;
}

/** The handles of one of a drawn wall's features, on the middle of its run: a
 *  door's top, a pane's top and bottom, an opening's sill and architrave — each
 *  its panel row's figure, from the ground, a bottom never at or over its top.
 *  They snap to the wall's top (a top) or its bottom (a bottom), first, and to its
 *  other features' tops (a pane rising with the wall's top is the wall's); a
 *  pane's dropped on the wall's follows it again. */
function wallFeatureHandles(w: Wall, id: string): Array<Handle<Wall>> {
  const sp = wallFeatureSpans(w).find((x) => x.f.id === id);
  if (!sp) return [];
  const { f, fr } = sp, c = (fr.oL - fr.oR) / 2;
  const line: [Vec2, Vec2] = [segPoint(fr, sp.s, c), segPoint(fr, sp.e, c)];
  const m = lerp(line[0], line[1], 0.5);
  const find = (x: Wall): WallFeature | undefined => x.features?.find((g) => g.id === id);
  const others = wallSnapHeights(w, id, false);
  const wb = wallBase(w), wt = wb + wallHeight(w);
  const ups = [{ z: wt, name: SNAP_NAMES.wallTop }, ...others], downs = [{ z: wb, name: SNAP_NAMES.wallBottom }, ...others];
  const out: Array<Handle<Wall>> = [];
  const put = (kind: HandleKind, z: number, height: HeightAxis<Wall>): void => {
    out.push({ key: `wallFeature:${id}:${kind}`, kind, z, points: [m], lines: [line], height });
  };
  const setTop = (x: Wall, v: number): CeilingNote | null => {
    const g = find(x);
    if (!g) return null;
    // A drawn wall's own top is its features' ceiling: a top stops there.
    v = underCap(v, wallBase(x) + wallHeight(x), wallFeatureTop(x, g));
    if (g.kind === 'glass' && Math.abs(v - (wallBase(x) + wallHeight(x))) < 0.5) delete g.height;
    else g.height = Math.max(wallFeatureBase(x, g) + 1, v);
    return null;
  };
  const setBase = (x: Wall, v: number): CeilingNote | null => {
    const g = find(x);
    if (!g) return null;
    const nb = Math.max(0, Math.min(v, wallFeatureTop(x, g) - 1));
    if (g.kind === 'glass' && Math.abs(nb - wallBase(x)) < 0.5) delete g.heightFromGround;
    else g.heightFromGround = nb;
    return null;
  };
  const top = wallFeatureTop(w, f), base = wallFeatureBase(w, f);
  if (f.kind === 'opening') {
    put('sill', base, { label: HANDLE_LABELS.sill, value: base, snaps: downs, set: setBase });
    put('architrave', top, { label: HANDLE_LABELS.architrave, value: top, snaps: ups, set: setTop });
  } else {
    put('top', top, { label: HANDLE_LABELS.top, value: top, snaps: ups, set: setTop });
    if (f.kind === 'glass') put('bottom', base, { label: HANDLE_LABELS.bottom, value: base, snaps: downs, set: setBase });
  }
  return out;
}

/** An extruded shape's handles (none for a flat one: it is not drawn in 3D or in
 *  a section), over its outline: its top — its Height, from its base — and its
 *  bottom — its Height from ground, the top staying where it is. */
export function polygonHandles(p: Polygon): Array<Handle<Polygon>> {
  const h = polygonHeight(p);
  if (h <= 0 || p.points.length < 3) return [];
  const b = polygonBase(p), at = insidePoint(p.points), area = p.points.slice();
  return [{
    key: 'top', kind: 'top', z: b + h, points: [at], area,
    height: { label: HANDLE_LABELS.top, value: h, from: b, snaps: [], set: (x, v) => { x.height = Math.max(1, Math.round(v)); return null; } },
  }, {
    key: 'bottom', kind: 'bottom', z: b, points: [at], area,
    height: {
      label: HANDLE_LABELS.bottom, value: b, snaps: [],
      set: (x, v) => {
        const b0 = polygonBase(x), t = b0 + polygonHeight(x), nb = Math.min(Math.round(v), t - 1);
        if (nb !== b0) { x.height = t - nb; x.heightFromGround = nb; }
        return null;
      },
    },
  }];
}

// ---- A connection's handles (docs/claude/connections.md *Drags and handles*) --

/** What a connection's handles read and write beyond themselves: one edit of
 *  it (connEdits.ts, against its drawing) and it resolved as it stands now. */
export interface ConnHandleCtx {
  edit: (c: Connection, e: ConnEdit) => boolean;
  geom: (c: Connection) => ConnGeom | null;
}

/** The selected connection's handles — in 3D, and a pipe's in the section views
 *  too —, one on each of its points (`connGeom`'s `ctrl`): a pipe's or a
 *  schematic line's, decided by what it resolves to. */
export function connHandles(g: ConnGeom, ctx: ConnHandleCtx): Array<Handle<Connection>> {
  return g.pipe ? pipeHandles(g, g.pipe, ctx) : lineHandles(g, ctx);
}

/** A pipe's handles: an end's height alone (where it stands is its glue); a
 *  bend's height and its place on its level, the first move deciding. A height
 *  snaps level with, at the fall from and at 45° to the points either side; a
 *  move in plan lines up square or at 45° with them (`snapPipeVertex`, read
 *  live: a bend carried at the fall moves with the one before it), held to a
 *  section's line in a section view; a hose's bend moves free. */
function pipeHandles(g: ConnGeom, pipe: ConnPipeGeom, ctx: ConnHandleCtx): Array<Handle<Connection>> {
  const ctrl = g.ctrl, slope = pipe.slope, n = ctrl.length;
  return ctrl.map((p, i): Handle<Connection> => {
    const snaps: NamedHeight[] = [];
    const from = (q: Pt3 | undefined, fall: number): void => {
      if (!q) return;
      const L = Math.hypot(p.x - q.x, p.y - q.y);
      snaps.push({ z: q.z, name: SNAP_NAMES.level });
      if (L < 0.5) return;
      if (slope > 0) snaps.push({ z: q.z + fall * slope * L, name: SNAP_NAMES.fall });
      snaps.push({ z: q.z + L, name: SNAP_NAMES.deg45 }, { z: q.z - L, name: SNAP_NAMES.deg45 });
    };
    from(ctrl[i - 1], -1); // falling from the point before…
    from(ctrl[i + 1], 1); // …and onto the point after
    const at: Vec2 = { x: p.x, y: p.y };
    const bend = i > 0 && i < n - 1;
    return {
      key: `bend:${i}`, kind: 'bend', z: p.z, points: [at], lines: [[at, at]],
      height: {
        label: i === 0 ? HANDLE_LABELS.fromHeight : i === n - 1 ? HANDLE_LABELS.toHeight : HANDLE_LABELS.bendHeight,
        value: p.z, snaps,
        set: (o, v) => { ctx.edit(o, { kind: 'pointZ', i, z: v }); return null; },
      },
      plan: bend ? {
        set: (o, d, snapMm, along) => {
          const q = { x: p.x + d.x, y: p.y + d.y, z: p.z };
          const motion: PipeMotion = along ? { kind: 'hline', o: q, u: along } : { kind: 'plane', z: p.z };
          const w = o.waypoints?.[i - 1], live = ctx.geom(o);
          if (!w || !live?.pipe) return null;
          const s = snapMm === 0 || live.pipe.flexible ? q : snapPipeVertex(q, live.ctrl[i - 1], live.ctrl[i + 1], live.pipe.slope, motion, snapMm).p;
          // Its height is written only when the motion changes it or it had one.
          ctx.edit(o, { kind: 'bendMove', i: i - 1, p: s, z: motion.kind !== 'plane' || w.z != null ? s.z : undefined });
          return null;
        },
        readout: (o) => {
          const w = o.waypoints?.[i - 1];
          return w ? { label: HANDLE_LABELS.bend, values: [w.x, w.y] } : null;
        },
      } : undefined,
    };
  });
}

/** A schematic line's handles in 3D (docs/claude/connections.md *Connections in
 *  3D*), one on every vertex of its 3D path: each end's height (where it stands
 *  is its glue); the top of each riser and every bend the run's height, which
 *  snaps back to the automatic one (then it follows the walls and roofs again);
 *  a bend's place in plan too, the first move deciding, lining up square or at
 *  45° with the points either side at the run's level. */
function lineHandles(g: ConnGeom, ctx: ConnHandleCtx): Array<Handle<Connection>> {
  if (!g.run) return [];
  const ctrl = g.ctrl, n = ctrl.length, runZ = g.run.z, auto = g.run.auto;
  const run = (): HeightAxis<Connection> => ({
    label: HANDLE_LABELS.run, value: runZ,
    snaps: [{ z: auto, name: SNAP_NAMES.autoRun }],
    set: (o, v) => { ctx.edit(o, { kind: 'runZ', z: v }); return null; },
  });
  const out: Array<Handle<Connection>> = [];
  ctrl.forEach((p, i) => {
    const at: Vec2 = { x: p.x, y: p.y };
    if (i === 0 || i === n - 1) {
      out.push({
        key: `bend:${i}`, kind: 'bend', z: p.z, points: [at], lines: [[at, at]],
        height: {
          label: i === 0 ? HANDLE_LABELS.fromHeight : HANDLE_LABELS.toHeight, value: p.z,
          snaps: [{ z: runZ, name: SNAP_NAMES.level }],
          set: (o, v) => { ctx.edit(o, { kind: 'pointZ', i, z: v }); return null; },
        },
      });
      out.push({ key: `rise:${i}`, kind: 'run', z: runZ, points: [at], lines: [[at, at]], height: run() });
      return;
    }
    out.push({
      key: `bend:${i}`, kind: 'run', z: runZ, points: [at], lines: [[at, at]], height: run(),
      plan: {
        set: (o, d, snapMm) => {
          const raw = { x: p.x + d.x, y: p.y + d.y, z: runZ };
          const q = snapMm === 0 ? raw : snapPipeVertex(raw, { ...ctrl[i - 1], z: runZ }, { ...ctrl[i + 1], z: runZ }, 0, { kind: 'plane', z: runZ }, snapMm).p;
          ctx.edit(o, { kind: 'bendMove', i: i - 1, p: q });
          return null;
        },
        readout: (o) => {
          const w = o.waypoints?.[i - 1];
          return w ? { label: HANDLE_LABELS.bend, values: [w.x, w.y] } : null;
        },
      },
    });
  });
  return out;
}

// ---- Where a handle stands --------------------------------------------------

/** 3D: the candidate point nearest the eye (its height `z`). */
export function nearestPoint(points: readonly Vec2[], z: number, eye: Pt3): Vec2 | null {
  let best: Vec2 | null = null, bd = Infinity;
  for (const p of points) {
    const d = Math.hypot(p.x - eye.x, p.y - eye.y, z - eye.z);
    if (d < bd) { bd = d; best = p; }
  }
  return best;
}

/** A section's frame: a point on its line, the line's unit direction and the
 *  unit normal toward what it views (`sectionFrame`), in world plan mm. */
export interface ViewFrame { a: Vec2; u: Vec2; n: Vec2 }
/** Where a handle stands in a section view: (t, h) of the view, how far beyond
 *  the line the figure it marks stands, and whether its plan axis slides there. */
export interface InView { t: number; h: number; depth: number; slides: boolean }

/** The least depth a figure may stand at and still be what a view shows (a
 *  millimetre in front of the line: what the line itself cuts). */
const NEAR_DEPTH = -1;

/** A handle in a section view, or null where the view does not show its figure:
 *  a level area stands over the middle of the part on the viewed side; lines at
 *  the piece nearest the viewer — where it crosses the line, or its middle when
 *  it runs along it; a plan-only handle only where its axis runs within 60° of
 *  the view's line. */
export function handleInView<T>(hd: Handle<T>, f: ViewFrame): InView | null {
  const slides = !hd.plan?.axis || Math.abs(dot(hd.plan.axis, f.u)) >= 0.5;
  if (!hd.height && (!hd.plan || !slides)) return null;
  const dep = (p: Vec2): number => dot(sub(p, f.a), f.n);
  const tOf = (p: Vec2): number => dot(sub(p, f.a), f.u);
  let best: { t: number; d: number } | null = null;
  if (hd.area) {
    const kept: Vec2[] = [];
    const ring = hd.area;
    ring.forEach((p, i) => {
      const q = ring[(i + 1) % ring.length], dp = dep(p), dq = dep(q);
      if (dp >= NEAR_DEPTH) kept.push(p);
      if ((dp >= NEAR_DEPTH) !== (dq >= NEAR_DEPTH)) kept.push(lerp(p, q, (NEAR_DEPTH - dp) / (dq - dp)));
    });
    if (kept.length >= 2) {
      const ts = kept.map(tOf);
      best = { t: (Math.min(...ts) + Math.max(...ts)) / 2, d: Math.min(...kept.map(dep)) };
    }
  } else {
    for (const [p, q] of hd.lines ?? []) {
      const dp = dep(p), dq = dep(q);
      if (dp < NEAR_DEPTH && dq < NEAR_DEPTH) continue;
      const cut = (s: number): Vec2 => lerp(p, q, s);
      const a = dp < NEAR_DEPTH ? cut((NEAR_DEPTH - dp) / (dq - dp)) : p;
      const b = dq < NEAR_DEPTH ? cut((NEAR_DEPTH - dp) / (dq - dp)) : q;
      const da = Math.max(NEAR_DEPTH, dp), db = Math.max(NEAR_DEPTH, dq);
      const at = Math.abs(da - db) < 1 ? lerp(a, b, 0.5) : da < db ? a : b;
      const d = Math.min(da, db);
      if (!best || d < best.d - 1e-6) best = { t: tOf(at), d };
    }
  }
  return best ? { t: best.t, h: hd.z, depth: best.d, slides } : null;
}

/** How near (mm in the view) two stacked handles stand to be one place. */
export const STACK_MM = 25;

/** The handles a section view shows (`handleInView`), less each stacked one that
 *  stands within {@link STACK_MM} of one of its stack nearer the viewer: a cage
 *  seen end on shows the layer of points nearest the line. */
export function handlesInView<T>(hds: ReadonlyArray<Handle<T>>, f: ViewFrame): Array<{ hd: Handle<T>; iv: InView }> {
  const all = hds.map((hd) => ({ hd, iv: handleInView(hd, f) })).filter((m): m is { hd: Handle<T>; iv: InView } => !!m.iv);
  return all.filter((m) => !m.hd.stack || !all.some((o) => o !== m && o.hd.stack === m.hd.stack
    && Math.hypot(o.iv.t - m.iv.t, o.iv.h - m.iv.h) < STACK_MM
    && (o.iv.depth < m.iv.depth - 1e-6 || (Math.abs(o.iv.depth - m.iv.depth) <= 1e-6 && all.indexOf(o) < all.indexOf(m)))));
}

// ---- The drag maths ------------------------------------------------------------

/** The height on the vertical line through `p` that the ray `o + s·d` passes
 *  closest to — its height where it passes the line, seen from above. Null when
 *  the ray looks (all but) straight down, where no height can be read. */
export function rayToVertical(o: Pt3, d: Pt3, p: Vec2): number | null {
  const hh = d.x * d.x + d.y * d.y, all = hh + d.z * d.z;
  if (!(all > 0) || hh / all < 1e-4) return null;
  const s = -((o.x - p.x) * d.x + (o.y - p.y) * d.y) / hh;
  return o.z + s * d.z;
}

/** How far along the line `p + s·a` (unit `a`) the ray `o + t·d` passes it
 *  closest: s. Null when the ray runs (all but) along the line, where no figure
 *  can be read. */
export function rayToAxis(o: Pt3, d: Pt3, p: Pt3, a: Pt3): number | null {
  const dl = Math.hypot(d.x, d.y, d.z);
  if (!(dl > 0)) return null;
  const dd = { x: d.x / dl, y: d.y / dl, z: d.z / dl };
  const b = a.x * dd.x + a.y * dd.y + a.z * dd.z, den = 1 - b * b;
  if (den < 1e-4) return null;
  const w = { x: p.x - o.x, y: p.y - o.y, z: p.z - o.z };
  return (b * (dd.x * w.x + dd.y * w.y + dd.z * w.z) - (a.x * w.x + a.y * w.y + a.z * w.z)) / den;
}

/** Where the ray `o + s·d` meets the level `z` ahead of it (plan point), or null. */
export function rayToLevel(o: Pt3, d: Pt3, z: number): Vec2 | null {
  if (Math.abs(d.z) < 1e-9) return null;
  const s = (z - o.z) / d.z;
  return s > 0 ? { x: o.x + s * d.x, y: o.y + s * d.y } : null;
}

/** Where a ray (drawing coordinates, unit `d`) enters a vertical prism over a
 *  convex plan ring between two heights — its distance, 0 from inside —, or null
 *  when it misses (Cyrus–Beck: the ray clipped by each face's half-space). */
export function rayIntoPrism(r: { o: Pt3; d: Pt3 }, v: { ring: readonly Vec2[]; z0: number; z1: number }): number | null {
  let t0 = 0, t1 = Infinity;
  // Inside: n · (p − a) ≤ 0 for each half-space (a point a on it, n its outward normal).
  const clip = (nx: number, ny: number, nz: number, ax: number, ay: number, az: number): boolean => {
    const num = nx * (r.o.x - ax) + ny * (r.o.y - ay) + nz * (r.o.z - az), den = nx * r.d.x + ny * r.d.y + nz * r.d.z;
    if (Math.abs(den) < 1e-12) return num <= 0;
    const t = -num / den;
    if (den < 0) t0 = Math.max(t0, t); else t1 = Math.min(t1, t);
    return t0 <= t1;
  };
  if (!clip(0, 0, -1, 0, 0, v.z0) || !clip(0, 0, 1, 0, 0, v.z1)) return null;
  const n = v.ring.length;
  let area = 0;
  for (let i = 0; i < n; i++) { const a = v.ring[i], b = v.ring[(i + 1) % n]; area += a.x * b.y - b.x * a.y; }
  const s = area >= 0 ? 1 : -1; // CCW: the outward normal of a→b is (dy, −dx)
  for (let i = 0; i < n; i++) {
    const a = v.ring[i], b = v.ring[(i + 1) % n];
    if (!clip(s * (b.y - a.y), -s * (b.x - a.x), 0, a.x, a.y, 0)) return null;
  }
  return Number.isFinite(t1) ? t0 : null;
}

/** How far (screen px) a handle that moves both ways must travel before its
 *  first move decides which. */
export const DECIDE_PX = 4;

/** Which way the first move of a two-way handle goes: up / down on screen (within
 *  45° of where its height rises, `up`) sets the height, anything else moves it
 *  in plan; null until it has travelled `DECIDE_PX`. */
export function firstMoveAxis(move: Vec2, up: Vec2): 'height' | 'plan' | null {
  const m = len(move);
  if (m < DECIDE_PX) return null;
  const u = len(up);
  if (u < 1e-9) return 'plan';
  return Math.abs(dot(move, up)) / (m * u) >= Math.SQRT1_2 ? 'height' : 'plan';
}

/** A plan move in whole steps of `step` mm: along the handle's axis when it has
 *  one (only that part of the move counts), else in the room's own frame (turned
 *  `rotation`° CCW), so a figure that starts round stays round. */
export function roundPlanDelta(d: Vec2, axis: Vec2 | undefined, rotation: number, step: number): Vec2 {
  const st = step > 0 ? step : 1;
  if (axis) return mul(axis, Math.round(dot(d, axis) / st) * st);
  const l = rotatePoint(d, -rotation);
  return rotatePoint({ x: Math.round(l.x / st) * st, y: Math.round(l.y / st) * st }, rotation);
}

/** A dragged figure snapped: free (Alt) to the whole mm; else to the nearest
 *  height within `tolMm` (what it caught is named), else to the zoom's round step. */
export function snapFigure(raw: number, snaps: readonly NamedHeight[], tolMm: number, stepMm: number, free: boolean): { v: number; caught?: NamedHeight } {
  if (free) return { v: Math.round(raw) };
  let best: NamedHeight | null = null;
  for (const s of snaps) if (Math.abs(s.z - raw) <= tolMm && (!best || Math.abs(s.z - raw) < Math.abs(best.z - raw))) best = s;
  if (best) return { v: best.z, caught: best };
  const st = stepMm > 0 ? stepMm : 1;
  return { v: Math.round(raw / st) * st };
}

// ---- The painter ---------------------------------------------------------------

/** A handle as drawn: its screen point, and `hot` while it is the one worked. */
export interface HandleMark { at: Vec2; hot?: boolean }
/** What a worked handle shows: the dashed vertical guide (floor → handle), a
 *  section's dashed level guide to the height it caught, and its figure. */
export interface HandleLive { guide?: [Vec2, Vec2]; snapGuide?: [Vec2, Vec2]; text: string[]; at: Vec2 }

/** The nearest mark within a grip's reach of `p`, or -1. */
export function handleMarkAt(marks: readonly HandleMark[], p: Vec2): number {
  let best = -1, bd = GRIP_HIT_PX;
  marks.forEach((m, i) => { const d = len(sub(m.at, p)); if (d <= bd) { bd = d; best = i; } });
  return best;
}

/** Paint what handles stand on (css px; a null point breaks its line): a cage's
 *  lines, dashed in its colour over a dark halo; `faint`, the bench's other
 *  cages, thin and see-through. */
export function drawGuideLines(ctx: CanvasRenderingContext2D, lines: ReadonlyArray<ReadonlyArray<Vec2 | null>>, faint = false): void {
  ctx.save();
  ctx.beginPath();
  for (const line of lines) {
    let open = false;
    for (const p of line) {
      if (!p) { open = false; continue; }
      if (open) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y);
      open = true;
    }
  }
  ctx.setLineDash([4, 3]);
  if (faint) ctx.globalAlpha = 0.35;
  ctx.strokeStyle = 'rgba(18,20,24,0.5)';
  ctx.lineWidth = faint ? 1.5 : 2.5;
  ctx.stroke();
  ctx.strokeStyle = CAGE_COLOR;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

const GUIDE = 'rgba(127, 178, 255, 0.9)';
const CAUGHT = '#aaff00';

/** Paint handles (grips; the worked one in the selected-vertex blue) and the
 *  worked one's guides and figure, the chip kept inside `w` × `h` css px. */
export function drawHandleMarks(ctx: CanvasRenderingContext2D, marks: readonly HandleMark[], live: HandleLive | null, w: number, h: number): void {
  ctx.save();
  const dashed = (a: Vec2, b: Vec2, color: string): void => {
    ctx.beginPath();
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = 'rgba(18,20,24,0.7)';
    ctx.lineWidth = 3;
    ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.25;
    ctx.stroke();
  };
  if (live?.guide) dashed(live.guide[0], live.guide[1], GUIDE);
  if (live?.snapGuide) dashed(live.snapGuide[0], live.snapGuide[1], CAUGHT);
  ctx.setLineDash([]);
  for (const m of marks) drawGrip(ctx, m.at, m.hot ? GRIP_SELECTED : GRIP_FILL);
  if (live && live.text.length) {
    ctx.font = '12px sans-serif';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    const padX = 6, padY = 4, lh = 15;
    const bw = Math.max(...live.text.map((l) => ctx.measureText(l).width)) + padX * 2, bh = live.text.length * lh + padY * 2;
    let bx = live.at.x + 14, by = live.at.y - bh / 2;
    if (w && bx + bw > w - 4) bx = live.at.x - 14 - bw;
    if (h) by = Math.max(4, Math.min(h - 4 - bh, by));
    ctx.fillStyle = 'rgba(20, 22, 28, 0.85)';
    ctx.strokeStyle = 'rgba(127, 178, 255, 0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(bx, by, bw, bh);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#e6eefc';
    live.text.forEach((l, i) => ctx.fillText(l, bx + padX, by + padY + i * lh));
  }
  ctx.restore();
}
